import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useCurrentPurchaseOrder } from '@/providers/PurchaseOrderProvider';
import { usePurchaseOrderComments, usePurchaseOrderCommentsInvalidate } from '@/hooks/api/0620_purchase_order_comments';
import { useEffect, useState } from 'react';
import { useCreatePurchaseOrderComment } from '@/hooks/api/0630_create_purchase_order_comment';
import { Comments as _Comments, OnCreateCommentProps, OnDeleteCommentProps, OnToggleReactionProps, OnUpdateCommentProps } from '@/components/element/Comments';
import { usePurchaseOrderCommentReaction } from '@/hooks/api/0631_purchase_order_comment_reactions';
import { MentionData } from '@draft-js-plugins/mention';
import { usePurchaseOrderTargetUsers } from '@/hooks/api/0541_purchase_order_target_users';
import { useUpdatePurchaseOrderComment } from '@/hooks/api/0632_update_purchase_order_comment';
import { useDeletePurchaseOrderComment } from '@/hooks/api/0633_destroy_purchase_order_comment';

export const Comments = () => {
  const { _w_, _t_, me } = useCurrentTeamMember();
  const { _po_ } = useCurrentPurchaseOrder();
  const [mentions, setMentions] = useState<MentionData[]>([]);
  const { data: comments } = usePurchaseOrderComments({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _po_!.id,
  });
  const { data: members } = usePurchaseOrderTargetUsers({ identifier: _w_!.identifier, team_id: _t_!.id, id: _po_!.id });
  const { mutate } = useCreatePurchaseOrderComment();
  const { mutate: updateMutate } = useUpdatePurchaseOrderComment();
  const { mutate: deleteMutate } = useDeletePurchaseOrderComment();
  const { invalidate: invalidateComments } = usePurchaseOrderCommentsInvalidate({ identifier: _w_!.identifier, team_id: _t_!.id, id: _po_!.id });
  const { mutate: reactionMutate } = usePurchaseOrderCommentReaction();

  useEffect(() => {
    if (members) {
      setMentions(() =>
        members.map((d) => ({
          id: d.id,
          name: d.user_account.name,
          avatar: d.user_account.icon_file_url,
        }))
      );
    }
  }, [members]);

  const onCreateComment = ({ content, content_json, setComment, setError, parent_id }: OnCreateCommentProps) => {
    mutate(
      {
        identifier: _w_!.identifier,
        team_id: _t_!.id,
        purchase_order_id: _po_!.id,
        content: content!,
        content_json: content_json,
        parent_id: parent_id,
      },
      {
        onSuccess: (res) => {
          setComment(res);
        },
        onError: (error: any) => {
          setError(error);
        },
        onSettled: () => {},
      }
    );
  };

  const onUpdateComment = ({ id, content, content_json, setComment, setError }: OnUpdateCommentProps) => {
    updateMutate(
      {
        identifier: _w_!.identifier,
        team_id: _t_!.id,
        purchase_order_id: _po_!.id,
        id: id,
        content: content!,
        content_json: content_json,
      },
      {
        onSuccess: (res) => {
          setComment(res);
        },
        onError: (error: any) => {
          setError(error);
        },
        onSettled: () => {},
      }
    );
  };

  const onDeleteComment = ({ id, setComment, setError }: OnDeleteCommentProps) => {
    deleteMutate(
      {
        identifier: _w_!.identifier,
        team_id: _t_!.id,
        purchase_order_id: _po_!.id,
        id: id,
      },
      {
        onSuccess: (res) => {
          setComment(res);
          invalidateComments();
        },
        onError: (error: any) => {
          setError(error);
        },
        onSettled: () => {},
      }
    );
  };

  const onToggleReaction = ({ comment_id, emoji_id, content, setComment, setError }: OnToggleReactionProps) => {
    reactionMutate(
      { identifier: _w_!.identifier, team_id: _t_!.id, purchase_order_id: _po_!.id, id: comment_id, emoji_id, content },
      {
        onSuccess: (res) => {
          setComment(res);
        },
        onError: (error: any) => setError(error),
        onSettled: () => {},
      }
    );
  };

  if (mentions.length == 0) return <></>;

  return (
    <_Comments
      identifier={`purchase_order_${_w_!.identifier}_${_t_!.id}_${_po_!.id}`}
      comments={comments}
      user={me!}
      mentions={mentions}
      onCreateComment={onCreateComment}
      onToggleReaction={onToggleReaction}
      onUpdateComment={onUpdateComment}
      onDeleteComment={onDeleteComment}
    />
  );
};
